import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useNotificationActivityLog } from "@/hooks/useNotificationActivityLog";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { CheckCircle, XCircle, Clock, Mail, MessageSquare, Bell, Play, Timer, Send, AlertCircle, SkipForward } from "lucide-react";

export function NotificationActivityLog() {
  const { logs, processingRuns, isLoading, isLoadingRuns } = useNotificationActivityLog({ limit: 25 });

  if (isLoading && isLoadingRuns) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case "email": return Mail;
      case "whatsapp": return MessageSquare;
      case "in_app": return Bell;
      default: return Mail;
    }
  };

  return (
    <div className="space-y-6">
      {/* Processing Runs Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Processing Runs</h3>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Timer className="h-4 w-4" />
            Last 10 runs
          </div>
        </div>

        {processingRuns.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground text-sm border rounded-md">
            No processing runs yet.
          </div>
        ) : (
          <div className="border rounded-md divide-y">
            {processingRuns.map((run) => (
              <div key={run.id} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <Badge variant={run.triggered_by === "manual" ? "default" : "secondary"} className="gap-1">
                    {run.triggered_by === "manual" ? <Play className="h-3 w-3" /> : <Timer className="h-3 w-3" />}
                    {run.triggered_by === "manual" ? "Run Now" : "Cron"}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {format(new Date(run.triggered_at), "MMM d, HH:mm:ss")}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1 text-sm">
                    <Send className="h-3.5 w-3.5 text-green-600" />
                    <span className="font-medium">{run.sent_count}</span>
                    <span className="text-muted-foreground">sent</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm">
                    <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                    <span className="font-medium">{run.failed_count}</span>
                    <span className="text-muted-foreground">failed</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm">
                    <SkipForward className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="font-medium">{run.skipped_count}</span>
                    <span className="text-muted-foreground">skipped</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Individual Notifications Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Recent Notifications</h3>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            Last 25 notifications
          </div>
        </div>

        {logs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No notifications have been sent yet. Activity will appear here once notifications are sent.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sent At</TableHead>
                <TableHead>Template</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Recipient</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => {
                const ChannelIcon = getChannelIcon(log.channel);
                return (
                  <TableRow key={log.id}>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(log.sent_at), "MMM d, HH:mm")}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {log.template?.name ?? "Unknown"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="gap-1">
                        <ChannelIcon className="h-3 w-3" />
                        {log.channel === "in_app" ? "In-App" : log.channel.charAt(0).toUpperCase() + log.channel.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {log.event?.title ?? "Unknown event"}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div>{log.profile?.first_name} {log.profile?.last_name}</div>
                        <div className="text-muted-foreground text-xs">{log.profile?.email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {log.status === "sent" ? (
                        <Badge variant="default" className="gap-1 bg-green-600">
                          <CheckCircle className="h-3 w-3" />
                          Sent
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="gap-1">
                          <XCircle className="h-3 w-3" />
                          Failed
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
